/* @noflow FIXME */
import idx from 'idx';
import * as React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { mountModal } from 'lib/modal';
import { debounce, capitalize } from 'lodash';
import AsyncSelect from 'components/AsyncSelect';
import AsyncSelectItem from 'components/AsyncSelectItem';
import OrganizationPickerItem from './OrganizationPickerItem';
import OrganizationPickerNewItem from './OrganizationPickerNewItem';
import { OrganizationCreateModal } from 'organizations/components/OrganizationCreate';
import OrganizationPickerSelectedItem, {
  OrganizationPickerSelectedLoadingItem,
} from './OrganizationPickerSelectedItem';
import type { Organization, OrganizationSearch } from './types'; // eslint-disable-line
import searchOrganizations from './searchOrganizations';

type Props = {
  /* The type of organizations to be
   * searched for.
   */
  type: OrganizationType,
  /* On change callback, called when a new
   * organization has been selected by the user.
   */
  onChange?: (organization?: Organization) => void,
  /* On click callback, called when any mouse iteraction happened,
   * on the input but also on the selected item.
   */
  onClickInteraction?: () => void,
  /* The initial organization uuid. When specified the
   * organization will be fetched on mount and displayed.
   */
  initialOrganizationUuid?: string,
  /* The item that will override the default permanent item and will
   * render as a child inside of an AsyncSelectItem at the bottom
   * of the org picker
   */
  permanentItem?: *,
  /* On click callback for the permanentItem, called when a permanentItem
   * exists and the user clicks it. Should only pass this prop when also
   * passing in a permanentItem.
   */
  permanentItemOnClick?: () => void,
  /* Private: True when the initial organization provided in the prop
   * is being loaded, this should only go from true -> false
   * when the component is mounting.
   */
  _loadingInitialOrganization: boolean,
  /* Private: The initial organization,
   * loaded from initialOrganizationUuid.
   */
  _initialOrganization: Organization,
  /* Private: The organization searcher.
   * Currently using v2 endpoint and writing result
   * into apollo caches.
   */
  _searchOrganizations: OrganizationSearch,
};

type State = {
  /* When an organization was picked, either
   * because of the initialOrganizationId or because
   * the user picked an organization after a search
   * we set this state attribute.
   */
  selectedOrganization?: Organization,
  /* When an organization is selected, the user is prompt
   * if he wants to "change" the organization, if he choose to
   * we then flip this boolean to true and show the search input.
   * This is because is edit mode we want to rollback to the previously
   * selected organization if the user unfocused or doesn't select a new organization.
   */
  editing: boolean,
  searchString: string,
};

class OrganizationPicker extends React.Component<Props, State> {
  state: State = {
    editing: false,
    searchString: '',
  };

  static defaultProps = {
    _searchOrganizations: searchOrganizations,
  };

  componentWillMount() {
    // set the selected organization when
    // the initial organization was already in apollo's cache
    if (this.props._initialOrganization) {
      this.setState({ selectedOrganization: this.props._initialOrganization });
    }
  }

  componentWillUpdate(nextProps: Props, nextState: State) {
    const currentID = idx(this.state, _ => _.selectedOrganization.id);
    const nextID = idx(nextState, _ => _.selectedOrganization.id);

    if (currentID !== nextID) {
      if (this.props.onChange) {
        this.props.onChange(nextState.selectedOrganization);
      }
    }

    // set the selected organization when
    // the apollo response came back for the initial organization
    if (!this.props._initialOrganization && nextProps._initialOrganization) {
      this.setState({ selectedOrganization: nextProps._initialOrganization });
    }
  }

  asyncSelect: *;

  /* The search function for the async select.
   * It queries the provider in search for organization matching the query string.
   * Should always be debounced to avoid making too many useless requests.
   */
  search = debounce((input: string, callback: Function) => {
    this.setState({ searchString: input });
    callback(
      this.props
        ._searchOrganizations({ query: input, type: this.props.type })
        .then(os => os.map(o => <OrganizationPickerItem organization={o} key={o.id} />))
    );
  }, 300);

  /* When called, display the organization create modal.
   * When the organization is created we set the selected organization
   * to the new organization.
   */
  mountOrganizationCreateModal = () => {
    const onCreate = (organization: Organization) => {
      this.setState({ selectedOrganization: organization });
    };

    mountModal(
      <OrganizationCreateModal
        onCreate={onCreate}
        organizationType={capitalize(this.props.type)}
        defaultOrganization={this.state.searchString}
      />,
      this
    );
  };

  /* Handle clicks of items in the async select dropdown.
   * If the selected item is the permanent item about create a new
   * organization, we display the creation modal, otherwise we set the
   * selected item and exit the editing mode (in case we were in edit mode).
   */
  handleSelect = (element: *) => {
    if (this.props.permanentItemOnClick && element.key === 'permanent_item') {
      this.props.permanentItemOnClick();
    } else if (element.key === 'create_organization') {
      this.mountOrganizationCreateModal();
    } else if (!(element.key === 'permanent_item') && !(element.key === 'create_organization')) {
      this.setState({ selectedOrganization: element.props.organization, editing: false });
    }
  };

  /* When user click "change" on the selected organization
   * We switch to edit mode, which will cause the async select
   * input to show up, ince that is done, we focus it!
   */
  handleChange = () => {
    this.setState({ editing: true }, () => this.asyncSelect.focus());

    // clicked on "change" link
    if (this.props.onClickInteraction) {
      this.props.onClickInteraction();
    }
  };

  /* On blur exit the edit mode
   * This will cause the previously selected
   * organization to show up if there was any selected organization.
   */
  handleBlur = () => {
    if (this.state.editing) {
      this.setState({ editing: false });
    }
  };

  render() {
    const { type } = this.props;

    if (this.props._loadingInitialOrganization) {
      return <OrganizationPickerSelectedLoadingItem />;
    } else if (this.state.selectedOrganization && !this.state.editing) {
      return (
        <OrganizationPickerSelectedItem
          onChange={this.handleChange}
          organization={this.state.selectedOrganization}
        />
      );
    } else {
      return (
        <AsyncSelect
          placeholder={`Search for a ${type}`}
          permanentItems={
            this.props.permanentItem
              ? [
                  <AsyncSelectItem title="" key="permanent_item">
                    {this.props.permanentItem}
                  </AsyncSelectItem>,
                ]
              : [<OrganizationPickerNewItem type={type} key="create_organization" />]
          }
          ref={a => (this.asyncSelect = a)}
          searchCallback={this.search}
          onSelect={this.handleSelect}
          onBlur={this.handleBlur}
          onClick={this.props.onClickInteraction}
        />
      );
    }
  }
}

export const QUERY = gql`
  query OrganizationPickerInitial($uuid: String!) {
    organizations(uuids: [$uuid]) {
      id
      uuid
      name
      city
      state
      members_sample_string
    }
  }
`;

export { OrganizationPicker };

export default graphql(QUERY, {
  skip: ({ initialOrganizationUuid }) => {
    return !initialOrganizationUuid;
  },
  options: ({ initialOrganizationUuid }) => {
    return {
      variables: {
        uuid: initialOrganizationUuid,
      },
    };
  },
  props: ({ data }) => {
    return {
      _loadingInitialOrganization: data.loading,
      _initialOrganization: idx(data, _ => _.organizations[0]),
    };
  },
})(OrganizationPicker);
